import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { UserPlus, Send, CheckCircle2, XCircle, Clock, Users, History, ScanLine, Trash2, UserCheck, DollarSign, Star } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import QRScannerModal from "./QRScannerModal";

export default function LoyaltyAdmin({ business, onChange }) {
  const [whatsapp, setWhatsapp] = useState("");
  const [inputMode, setInputMode] = useState("pesos"); // "pesos" | "puntos"
  const [amount, setAmount] = useState(""); // pesos or points depending on mode
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const computedPoints = inputMode === "pesos"
    ? Math.floor(Number(amount) / 100)
    : Number(amount);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [lookup, setLookup] = useState(null); // {customer_name, current_balance}
  const [lookupErr, setLookupErr] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const lookupTimer = useRef(null);

  const [redemptions, setRedemptions] = useState([]);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const load = async () => {
    const [r, b, t] = await Promise.all([
      api.get("/business/loyalty/redemptions"),
      api.get("/business/loyalty/balances"),
      api.get("/business/loyalty/transactions"),
    ]);
    setRedemptions(r.data);
    setBalances(b.data);
    setTransactions(t.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // Auto-lookup when whatsapp reaches 10 digits
  useEffect(() => {
    setLookup(null); setLookupErr("");
    const digits = whatsapp.replace(/\D/g, "");
    if (digits.length < 10) return;
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/business/loyalty/customer-lookup?whatsapp=${digits}`);
        setLookup(data); setLookupErr("");
      } catch (e) {
        setLookup(null);
        setLookupErr(e.response?.data?.detail || "Cliente no registrado");
      }
    }, 400);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  }, [whatsapp]);

  const handleScan = (digits) => {
    setScannerOpen(false);
    setWhatsapp(digits);
    toast.success("Código escaneado");
  };

  const award = async (e) => {
    e.preventDefault();
    const digits = whatsapp.replace(/\D/g, "");
    if (digits.length < 10) return toast.error("Captura el celular completo (10 dígitos)");
    if (!amount || computedPoints < 1) return toast.error(
      inputMode === "pesos" ? "El monto debe ser al menos $100" : "Ingresa al menos 1 punto"
    );
    setBusy(true);
    try {
      const { data } = await api.post("/business/loyalty/award", {
        customer_whatsapp: digits,
        points: computedPoints,
        note: note.trim(),
      });
      toast.success(`+${data.awarded_points} pts a ${data.customer_name || data.customer_whatsapp}. Balance: ${data.new_balance}`);
      setWhatsapp(""); setAmount(""); setNote(""); setLookup(null);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al otorgar puntos");
    } finally { setBusy(false); }
  };

  const claim = async (id) => {
    try {
      await api.post(`/business/loyalty/redemptions/${id}/claim`);
      toast.success("Canje marcado como entregado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const refund = async (id) => {
    try {
      await api.post(`/business/loyalty/redemptions/${id}/refund`);
      toast.success("Canje cancelado y puntos devueltos");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const resetBalance = async () => {
    if (!resetTarget) return;
    try {
      await api.post(`/business/loyalty/balances/${resetTarget.customer_id}/reset`);
      toast.success(`Puntos de ${resetTarget.customer_name} reiniciados a 0`);
      setResetTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const pending = redemptions.filter((r) => r.status === "pending");
  const history = redemptions.filter((r) => r.status !== "pending");

  return (
    <div className="space-y-6">
      {/* Award form */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#7C3AED]" />
          <h3 className="font-display text-2xl tracking-tight">Otorgar puntos a un cliente</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Pide al cliente su celular o escanea su código QR. Si no está registrado, dile que entre a <strong>/registro-cliente</strong>.
        </p>
        <div className="mt-4">
          <Button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="btn-primary rounded-full px-6 py-5 text-base"
            data-testid="qr-scan-btn"
            disabled={busy}
          >
            <ScanLine className="w-5 h-5 mr-2" />
            {busy ? "Procesando…" : "Escanear QR del cliente"}
          </Button>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={() => { setInputMode("pesos"); setAmount(""); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${inputMode === "pesos" ? "bg-[#E05A47] text-white border-[#E05A47]" : "border-border text-muted-foreground hover:border-[#E05A47]/50"}`}
            data-testid="mode-pesos"
          >
            <DollarSign className="w-3.5 h-3.5" /> Monto
          </button>
          <button
            type="button"
            onClick={() => { setInputMode("puntos"); setAmount(""); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${inputMode === "puntos" ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "border-border text-muted-foreground hover:border-[#7C3AED]/50"}`}
            data-testid="mode-puntos"
          >
            <Star className="w-3.5 h-3.5" /> Puntos
          </button>
        </div>

        <form onSubmit={award} className="mt-3 grid md:grid-cols-12 gap-3" data-testid="award-form">
          <div className="md:col-span-5">
            <Label className="text-xs">Celular del cliente</Label>
            <Input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 12))}
              inputMode="numeric"
              placeholder="5512345678"
              className="mt-1"
              data-testid="award-whatsapp"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">
              {inputMode === "pesos" ? "Monto de compra ($)" : "Puntos a otorgar"}
            </Label>
            <div className="relative mt-1">
              <Input
                type="number"
                min={inputMode === "pesos" ? "100" : "1"}
                step={inputMode === "pesos" ? "1" : "1"}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={inputMode === "pesos" ? "350" : "3"}
                className="mt-0"
                data-testid="award-amount"
              />
              {amount && inputMode === "pesos" && (
                <div className={`mt-1 text-xs font-medium ${computedPoints >= 1 ? "text-[#5B21B6]" : "text-zinc-400"}`}>
                  = {computedPoints} {computedPoints === 1 ? "punto" : "puntos"}
                  {computedPoints < 1 && " (mínimo $100)"}
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Nota (opcional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tacos 2x1" className="mt-1" data-testid="award-note" />
          </div>
          <div className="md:col-span-2 flex items-end pb-0.5">
            <Button type="submit" disabled={busy || !lookup || computedPoints < 1} className="btn-primary rounded-full w-full" data-testid="award-submit">
              <Send className="w-4 h-4 mr-1" />
              {busy ? "Enviando…" : "Otorgar"}
            </Button>
          </div>
        </form>

        {/* Customer lookup preview */}
        {lookup && (
          <div className="mt-4 bg-gradient-to-r from-[#EDE9FE] to-[#F5F3FF] border border-[#C4B5FD] rounded-xl p-4 flex items-center gap-3" data-testid="customer-lookup-preview">
            <div className="w-10 h-10 rounded-full bg-white grid place-items-center border border-[#C4B5FD]">
              <UserCheck className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">Otorgando puntos a</div>
              <div className="font-display text-xl text-[#0E0E12] leading-tight">{lookup.customer_name}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Balance actual: <strong className="text-[#5B21B6]">{lookup.current_balance} pts</strong> · Total ganado: {lookup.total_earned}
              </div>
              {computedPoints >= 1 && (
                <div className="text-xs font-medium text-[#E05A47] mt-1">
                  +{computedPoints} {computedPoints === 1 ? "punto" : "puntos"} a otorgar
                  {inputMode === "pesos" && amount ? ` (${new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount)})` : ""}
                </div>
              )}
            </div>
          </div>
        )}
        {lookupErr && whatsapp.replace(/\D/g, "").length >= 10 && (
          <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm text-zinc-700" data-testid="customer-lookup-error">
            {lookupErr}
          </div>
        )}
      </div>

      {/* Pending redemptions */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-[#7C3AED]" />
          <h3 className="font-display text-2xl tracking-tight">Canjes pendientes</h3>
          {pending.length > 0 && <Badge className="bg-zinc-200 text-zinc-900">{pending.length}</Badge>}
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-muted-foreground">Sin canjes pendientes por entregar.</div>
        ) : (
          <ul className="divide-y divide-border">
            {pending.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3" data-testid={`pending-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{r.customer_name || r.customer_whatsapp}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.customer_whatsapp} · {new Date(r.created_at).toLocaleString("es-MX")}</div>
                  <div className="text-sm mt-1">🎁 {r.reward} <span className="text-muted-foreground">({r.points_used} pts)</span></div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => refund(r.id)} data-testid={`refund-${r.id}`}>
                    <XCircle className="w-4 h-4 mr-1" />Cancelar
                  </Button>
                  <Button className="btn-primary" size="sm" onClick={() => claim(r.id)} data-testid={`claim-${r.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />Entregado
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Clientes con puntos */}
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-[#7C3AED]" />
            <h3 className="font-display text-xl">Mis clientes con puntos</h3>
          </div>
          {balances.length === 0 ? (
            <div className="text-sm text-muted-foreground">Aún no has otorgado puntos.</div>
          ) : (
            <ul className="divide-y divide-border max-h-80 overflow-auto">
              {balances.slice(0, 50).map((b) => (
                <li key={b.id} className="py-2.5 flex items-center justify-between text-sm gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{b.customer_name || b.customer_whatsapp}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{b.customer_whatsapp}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[#5B21B6] font-semibold">{b.points_balance} pts</div>
                    <div className="text-[10px] text-muted-foreground">Total: {b.total_earned}</div>
                  </div>
                  {b.points_balance > 0 && (
                    <button
                      onClick={() => setResetTarget({ customer_id: b.customer_id, customer_name: b.customer_name || b.customer_whatsapp, current: b.points_balance })}
                      className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      title="Reiniciar puntos (premio entregado)"
                      data-testid={`reset-${b.customer_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Movimientos */}
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#7C3AED]" />
            <h3 className="font-display text-xl">Movimientos recientes</h3>
          </div>
          {transactions.length === 0 ? (
            <div className="text-sm text-muted-foreground">Sin movimientos.</div>
          ) : (
            <ul className="divide-y divide-border max-h-80 overflow-auto">
              {transactions.slice(0, 30).map((t) => (
                <li key={t.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="truncate">{t.customer_name || t.customer_email || t.customer_id?.slice(0, 8)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("es-MX")} {t.note ? `· ${t.note}` : ""}</div>
                  </div>
                  <div className={`font-mono font-semibold ${t.points >= 0 ? "text-[#5B21B6]" : "text-zinc-700"}`}>
                    {t.points > 0 ? `+${t.points}` : t.points}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="text-xs text-muted-foreground text-center">
          {history.length} canjes históricos
        </div>
      )}
    </div>
  );
}
